import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { ApiError } from '../../lib/api';
import type { Account } from '../../types/account';
import { listBackfills, triggerBackfill } from './api';
import { accountHasRunningBackfill, anyBackfillRunning } from './backfillView';

// Build B — the account-page backfill control (rendered admin-only by
// AccountDetail's Reporting section). Shows the recurring-sync status for
// context and a "Backfill history" button that fires a full 24-month Meta pull
// after a confirm. While this account has a running backfill the button is
// replaced with a live "running" state + a link to the Backfills page.

type Props = { account: Account };

export function AccountBackfillPanel({ account }: Props) {
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Shares the Backfills page's cache key; polls while any backfill runs so this
  // account's button re-enables the moment its run finishes.
  const backfills = useQuery({
    queryKey: ['reporting', 'backfills'],
    queryFn: listBackfills,
    refetchInterval: (q) => (anyBackfillRunning(q.state.data?.backfills ?? []) ? 10000 : false),
  });

  const running = accountHasRunningBackfill(backfills.data?.backfills ?? [], account.id);

  const trigger = useMutation({
    mutationFn: () => triggerBackfill(account.id),
    onSuccess: () => {
      setConfirmOpen(false);
      void qc.invalidateQueries({ queryKey: ['reporting', 'backfills'] });
      toast.success('Backfill started', {
        description: `Pulling ${account.name}'s full Meta history.`,
        action: { label: 'View', onClick: () => window.location.assign('/reporting/backfills') },
      });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Failed to start backfill.');
    },
  });

  return (
    <div className="space-y-3">
      <dl>
        <div className="flex gap-2 py-1 text-sm">
          <dt className="w-48 shrink-0 text-slate-500">Recurring reporting sync</dt>
          <dd className="text-slate-900">
            {(account.reportingSyncEnabled ?? false) ? 'On' : 'Off'}
          </dd>
        </div>
      </dl>

      <div className="flex flex-wrap items-center gap-3">
        {running ? (
          <span className="inline-flex items-center gap-2 text-sm text-amber-700">
            <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" />
            Backfill running…
          </span>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)}>
            Backfill history
          </Button>
        )}
        <Link to="/reporting/backfills" className="text-sm text-sky-700 underline hover:text-sky-900">
          View all backfills
        </Link>
      </div>

      <p className="text-xs text-slate-500">
        Pulls the full 24-month Meta history for this account into the reporting warehouse. Heavy
        operation — runs in the background; watch progress on the Backfills page.
      </p>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl p-6">
            <h2 className="mb-1 text-lg font-semibold text-slate-900">Backfill history</h2>
            <p className="mb-4 text-sm text-slate-600">
              Pull the full 24-month history for{' '}
              <span className="font-medium">{account.name}</span> from Meta? This fires a background
              job and can take a while for a large account.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setConfirmOpen(false)}
                disabled={trigger.isPending}
              >
                Cancel
              </Button>
              <Button onClick={() => trigger.mutate()} disabled={trigger.isPending}>
                {trigger.isPending ? 'Starting…' : 'Start backfill'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
